import { useState } from 'react';
import { useContainerWidth } from '../../hooks/useContainerWidth';

export interface StackedBar {
  /** Stable key (e.g. "2026-09"). */
  key: string;
  /** Axis label (e.g. "Sep 2026"). */
  label: string;
  /** Series -> value. Missing series are treated as 0. */
  values: Record<string, number>;
}

interface Props {
  bars: StackedBar[];
  /** Fixed series order; the first series sits at the baseline. */
  series: string[];
  colorOf: (series: string) => string;
  formatValue: (n: number) => string;
  height?: number;
  ariaLabel: string;
}

const PAD = { top: 12, right: 12, bottom: 28, left: 52 };
const GAP = 2; // surface gap between stacked segments

/** Thin stacked bars with a recessive grid, per-bar hover tooltip and a legend rendered by the parent. */
export function StackedBarChart({ bars, series, colorOf, formatValue, height = 220, ariaLabel }: Props) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);

  const totals = bars.map(b => series.reduce((s, k) => s + (b.values[k] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const niceMax = niceCeil(max);
  const plotW = width - PAD.left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const slot = bars.length > 0 ? plotW / bars.length : plotW;
  const barW = Math.min(40, Math.max(10, slot * 0.55));
  const y = (v: number) => PAD.top + plotH - (v / niceMax) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map(f => f * niceMax);

  return (
    <div ref={ref} className="relative w-full">
      <svg width={width} height={height} role="img" aria-label={ariaLabel} className="block select-none">
        {ticks.map(t => (
          <g key={t}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
            <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#64748b">
              {formatValue(t)}
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const cx = PAD.left + slot * i + slot / 2;
          const x = cx - barW / 2;
          let cursor = PAD.top + plotH;
          const isHover = hover === i;
          return (
            <g key={b.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              {/* hit target wider than the mark */}
              <rect x={PAD.left + slot * i} y={PAD.top} width={slot} height={plotH} fill="transparent" />
              {series.map((s, si) => {
                const v = b.values[s] ?? 0;
                if (v <= 0) return null;
                const h = Math.max(0, (v / niceMax) * plotH - GAP);
                const top = cursor - h - (si === 0 ? 0 : GAP);
                cursor = top;
                const isTop = series.slice(si + 1).every(k => (b.values[k] ?? 0) <= 0);
                return (
                  <path
                    key={s}
                    d={segmentPath(x, top, barW, h, isTop ? 4 : 0)}
                    fill={colorOf(s)}
                    opacity={hover === null || isHover ? 1 : 0.45}
                  />
                );
              })}
              <text x={cx} y={height - 8} textAnchor="middle" fontSize={11} fill="#475569">
                {b.label}
              </text>
            </g>
          );
        })}
      </svg>
      {hover !== null && bars[hover] && (
        <div
          className="pointer-events-none absolute z-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow"
          style={{ left: Math.min(width - 180, Math.max(0, PAD.left + slot * hover + slot / 2 - 80)), top: 0 }}
        >
          <div className="font-medium text-slate-800">{bars[hover].label}</div>
          {series.filter(s => (bars[hover].values[s] ?? 0) > 0).map(s => (
            <div key={s} className="flex items-center gap-2 text-slate-600">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(s) }} />
              <span className="flex-1">{s}</span>
              <span className="tabular-nums">{formatValue(bars[hover].values[s] ?? 0)}</span>
            </div>
          ))}
          <div className="mt-1 border-t border-slate-100 pt-1 text-slate-800 flex justify-between gap-4">
            <span>Total</span>
            <span className="tabular-nums">{formatValue(totals[hover])}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** Rectangle with optional rounded top corners (data-end), square at the baseline. */
function segmentPath(x: number, y: number, w: number, h: number, r: number): string {
  const rr = Math.min(r, h / 2, w / 2);
  if (rr <= 0) return `M${x},${y}h${w}v${h}h${-w}z`;
  return `M${x},${y + rr}a${rr},${rr} 0 0 1 ${rr},${-rr}h${w - 2 * rr}a${rr},${rr} 0 0 1 ${rr},${rr}v${h - rr}h${-w}z`;
}

function niceCeil(v: number): number {
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  const f = v / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;
  return nice * exp;
}
